import React from "react";
import { CloseOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Input } from "antd";
import dayjs from "dayjs";
import styled from "styled-components";
import { AppDatePicker } from "@/shared/components";
import { TAP_TARGET, mediaPhone } from "@/shared/theme/breakpoints";
import { Icon } from "@/shared/components/Icon/Icon";
import {
	chronologyErrorKey,
	itemErrorKey,
	type DeliverableItem,
	type DeliverableRow,
} from "../../hooks/engagement-terms-tab/engagementTermsState";
import { formatDisplayDate } from "./constants";
import {
	RequiredMark,
	SectionCopy,
	SectionDescription,
	SectionHeader,
	SectionTitle,
	SectionTitleRow,
	TermsSection,
	TermsStatusBadge,
} from "./SectionPrimitives";
import type {
	EngagementDeliverablesController,
	EngagementTermsSectionStatusProps,
	RegisterValidationTarget,
} from "./types";

const DeliverableList = styled.div`
	margin-top: 24px;
	padding-left: 8px;

	${mediaPhone`
		margin-top: 12px;
		padding-left: 4px;
	`}
`;

const DeliverableCard = styled.div`
	position: relative;
	padding: 0 0 20px 32px;

	&::before {
		content: "";
		position: absolute;
		left: 8px;
		top: 12px;
		bottom: 4px;
		width: 1px;
		border-left: 1px dashed #9fadf1;
	}

	&::after {
		content: "";
		position: absolute;
		left: 4px;
		top: 4px;
		width: 8px;
		height: 8px;
		border-radius: 50%;
		background: #4460ef;
		box-shadow: 0 0 0 1px #3a0ca3;
	}
`;

const DeliverableNameRow = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
	min-height: 20px;
`;

const DeliverableName = styled.div`
	font-size: 14px;
	font-weight: 600;
	color: #222;
`;

const IconAction = styled.button`
	display: inline-grid;
	place-items: center;
	width: 20px;
	height: 20px;
	padding: 0;
	border: 0;
	background: transparent;
	color: #4460ef;
	cursor: pointer;
	font-size: 16px;

	&:hover {
		color: #3a0ca3;
	}
`;

const DeliverableMeta = styled.div`
	display: flex;
	flex-direction: column;
	align-items: start;
	flex-wrap: wrap;
	gap: 6px;
	margin-top: 2px;
	font-size: 12px;
	color: #707070;
`;

const DeliverableTypeDescription = styled.span`
	color: #707070;
`;

const DateChip = styled.span`
	display: inline-flex;
	align-items: center;
	min-height: 22px;
	padding: 2px 6px;
	border: 1px solid #c8b8fb;
	border-radius: 4px;
	background: #f6f2ff;
	color: #7047eb;
	font-size: 12px;
	font-weight: 500;
`;

const formatDeliverableType = (deliverableType: string): string => {
	if (/^\d+ days$/.test(deliverableType)) return `${deliverableType} Ad Spark Code`;
	if (deliverableType.startsWith("Crosspost to ")) {
		return deliverableType.replace("Crosspost to ", "Crossposting to ");
	}
	return deliverableType;
};

const ItemsGrid = styled.div<{ $locked: boolean }>`
	display: grid;
	grid-template-columns: ${(props) =>
		props.$locked ? "minmax(180px, 1fr) 180px" : "16px minmax(180px, 1fr) 180px 24px"};
	column-gap: 8px;
	row-gap: 6px;
	margin-top: 16px;
	font-size: 12px;

	/* Phones: items stack, one row per item, instead of a 400px-wide table. */
	${mediaPhone`
		grid-template-columns: minmax(0, 1fr);
		row-gap: 12px;
		margin-top: 12px;
	`}
`;

const GridHead = styled.div`
	padding: 0 12px 2px;
	color: #707070;

	${mediaPhone`
		display: none;
	`}
`;

const ItemRowLayout = styled.div<{ $locked: boolean }>`
	grid-column: 1 / -1;
	display: grid;
	grid-template-columns: ${(props) =>
		props.$locked ? "minmax(180px, 1fr) 180px" : "16px minmax(180px, 1fr) 180px 24px"};
	column-gap: 8px;
	align-items: start;

	/* Phones: name and remove side by side, the due date under them (no remove column when locked). */
	${(props) => mediaPhone`
		grid-template-columns: ${props.$locked ? "minmax(0, 1fr)" : "minmax(0, 1fr) auto"};
		grid-template-areas: ${props.$locked ? '"name" "date"' : '"name remove" "date date"'};
		row-gap: 6px;

		[data-slot="name"] {
			grid-area: name;
		}

		[data-slot="date"] {
			grid-area: date;
		}

		[data-slot="remove"] {
			grid-area: remove;
		}
	`}
`;

const DragHandle = styled.span`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 28px;
	color: #d3d3d3;
	cursor: grab;

	&:active {
		cursor: grabbing;
	}

	/* HTML5 drag and drop doesn't fire on touch, so the handle would do nothing. */
	${mediaPhone`
		display: none;
	`}
`;

const DragDots = styled.span`
	display: grid;
	grid-template-columns: repeat(2, 3px);
	grid-template-rows: repeat(3, 3px);
	gap: 2px;

	span {
		width: 3px;
		height: 3px;
		border-radius: 50%;
		background: #b8b8b8;
	}
`;

const ItemInputWrap = styled.div<{ $invalid?: boolean }>`
	min-width: 0;

	.ant-input,
	.app-date-picker {
		width: 100%;
		height: 28px;
		padding: 2px 10px;
		border-color: ${(props) => (props.$invalid ? "#f72586" : "#cdd4f6")} !important;
		border-radius: 6px;
		box-shadow: none !important;
		font-size: 12px;
	}

	.app-date-picker input {
		font-size: 12px;
	}

	${mediaPhone`
		.ant-input,
		.app-date-picker {
			height: ${TAP_TARGET}px;
			padding: 2px 12px;
			font-size: 16px;
		}

		.app-date-picker input {
			font-size: 16px;
		}
	`}
`;

const ItemError = styled.div`
	margin-top: 3px;
	color: #d80027;
	font-size: 10px;
	line-height: 14px;
`;

const RemoveItemButton = styled.button`
	display: grid;
	place-items: center;
	width: 24px;
	height: 28px;
	padding: 0;
	border: 0;
	background: transparent;
	color: #4460ef;
	cursor: pointer;
	font-size: 12px;

	${mediaPhone`
		width: ${TAP_TARGET}px;
		height: ${TAP_TARGET}px;
	`}
`;

const AddItemButton = styled.button`
	grid-column: 2 / 3;
	display: inline-flex;
	align-items: center;
	gap: 6px;
	justify-self: start;
	padding: 2px 4px;
	border: 0;
	background: transparent;
	color: #454545;
	font: 600 12px var(--font-family-app);
	cursor: pointer;

	${mediaPhone`
		grid-column: 1 / -1;
		min-height: ${TAP_TARGET}px;
	`}
`;

const GoLiveRow = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	gap: 3px;
	min-height: 48px;
	margin-top: 18px;

	&::before {
		content: "";
		position: absolute;
		left: -28px;
		top: 5px;
		width: 8px;
		height: 8px;
		border-radius: 50%;
		background: #9fadf1;
		box-shadow: 0 0 0 1px #3a0ca3;
	}

	&::after {
		content: "";
		position: absolute;
		left: -26px;
		top: 15px;
		bottom: -20px;
		width: 4px;
		background: #fff;
		pointer-events: none;
	}
`;

const GoLiveTitle = styled.div`
	display: flex;
	align-items: center;
	gap: 5px;
	font-size: 14px;
	font-weight: 600;
	color: #222;
`;

const LiveGlyph = styled.span`
	color: #f72586;
	font-size: 12px;
	letter-spacing: -2px;
`;

const AddDeliverable = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	margin: 0 0 0 32px;
	padding: 4px;
	border: 0;
	background: transparent;
	color: #454545;
	font: 600 12px var(--font-family-app);
	cursor: pointer;
`;

interface DeliverableItemRowProps {
	deliverable: DeliverableItem;
	item: DeliverableRow;
	locked: boolean;
	editor: EngagementDeliverablesController;
	registerValidationTarget: RegisterValidationTarget;
}

const DeliverableItemRow: React.FC<DeliverableItemRowProps> = ({
	deliverable,
	item,
	locked,
	editor,
	registerValidationTarget,
}) => {
	const nameErrorKey = itemErrorKey(deliverable.id, item.id, "name");
	const dueDateErrorKey = itemErrorKey(deliverable.id, item.id, "dueDate");
	const chronologyKey = chronologyErrorKey(deliverable.id, item.id);
	const nameInvalid = editor.invalidItems.has(nameErrorKey);
	const dueDateMissing = editor.invalidItems.has(dueDateErrorKey) && !item.dueDate;
	const chronologyInvalid = editor.chronologyErrors.has(chronologyKey);

	return (
		<ItemRowLayout
			$locked={locked}
			onDragOver={(event) => event.preventDefault()}
			onDrop={() => editor.dropDeliverableItem(deliverable.id, item.id)}
		>
			{!locked && (
				<DragHandle
					draggable
					onDragStart={() => editor.beginDrag(deliverable.id, item.id)}
					onDragEnd={editor.endDrag}
					aria-label={`Reorder item ${item.name || item.id}`}
				>
					<DragDots aria-hidden="true">
						{Array.from({ length: 6 }, (_, index) => (
							<span key={index} />
						))}
					</DragDots>
				</DragHandle>
			)}
			<ItemInputWrap data-slot="name" ref={registerValidationTarget(nameErrorKey)} $invalid={nameInvalid}>
				<Input
					value={item.name}
					aria-label={`${deliverable.name} item name`}
					onChange={(event) =>
						editor.updateDeliverableItem(deliverable.id, item.id, "name", event.target.value)
					}
				/>
				{nameInvalid && <ItemError>Item is required</ItemError>}
			</ItemInputWrap>
			<ItemInputWrap
				data-slot="date"
				ref={registerValidationTarget(dueDateErrorKey)}
				$invalid={dueDateMissing || chronologyInvalid}
			>
				<AppDatePicker
					value={item.dueDate ? dayjs(item.dueDate) : null}
					format="MMM D, YYYY"
					aria-label={`${deliverable.name} item due date`}
					suffixIcon={
						locked ? undefined : <Icon src="/icon/cet-calendar.svg" size={16} preserveOriginalColor />
					}
					onChange={(date) =>
						editor.updateDeliverableItem(
							deliverable.id,
							item.id,
							"dueDate",
							date?.format("YYYY-MM-DD") || "",
						)
					}
				/>
				{dueDateMissing && <ItemError>Due date is required</ItemError>}
				{chronologyInvalid && <ItemError>Dates must run from oldest to latest</ItemError>}
			</ItemInputWrap>
			{!locked && (
				<RemoveItemButton
					data-slot="remove"
					type="button"
					aria-label={`Remove item ${item.name || item.id}`}
					onClick={() => editor.removeDeliverableItem(deliverable.id, item.id)}
				>
					<CloseOutlined />
				</RemoveItemButton>
			)}
		</ItemRowLayout>
	);
};

interface Props extends EngagementTermsSectionStatusProps {
	editor: EngagementDeliverablesController;
	locked: boolean;
	registerValidationTarget: RegisterValidationTarget;
}

export const DeliverablesSection: React.FC<Props> = ({
	editor,
	locked,
	showStatusBadges,
	customizedFields,
	registerValidationTarget,
}) => (
	<TermsSection>
		<SectionHeader>
			<Icon src="/icon/cet-schedule.svg" size={24} preserveOriginalColor />
			<SectionCopy>
				<SectionTitleRow>
					<SectionTitle>Schedule &amp; Deliverables</SectionTitle>
					{showStatusBadges && <TermsStatusBadge field="deliverables" customizedFields={customizedFields} />}
				</SectionTitleRow>
				<SectionDescription>Planned schedules of all the deliverables.</SectionDescription>
			</SectionCopy>
		</SectionHeader>
		<DeliverableList>
			{editor.deliverables.map((deliverable, deliverableIndex) => (
				<DeliverableCard key={deliverable.id}>
					<DeliverableNameRow>
						<DeliverableName>{deliverable.name}</DeliverableName>
						{!locked && (
							<IconAction
								type="button"
								aria-label={`Edit ${deliverable.name}`}
								onClick={() => editor.openEditDeliverable(deliverable)}
							>
								<EditOutlined />
							</IconAction>
						)}
					</DeliverableNameRow>
					<DeliverableMeta>
						<DeliverableTypeDescription>
							{deliverable.deliverableTypes.map(formatDeliverableType).join(" • ")}
						</DeliverableTypeDescription>
						<DateChip>Due date: {formatDisplayDate(deliverable.dueDate) || "Select date"}</DateChip>
					</DeliverableMeta>
					<ItemsGrid $locked={locked}>
						{!locked && <span />}
						<GridHead>
							Items<RequiredMark aria-hidden="true">*</RequiredMark>
						</GridHead>
						<GridHead>
							Due dates<RequiredMark aria-hidden="true">*</RequiredMark>
						</GridHead>
						{!locked && <span />}
						{deliverable.items.map((item) => (
							<DeliverableItemRow
								key={item.id}
								deliverable={deliverable}
								item={item}
								locked={locked}
								editor={editor}
								registerValidationTarget={registerValidationTarget}
							/>
						))}
						{!locked && (
							<AddItemButton type="button" onClick={() => editor.addDeliverableItem(deliverable.id)}>
								<PlusOutlined style={{ color: "#4460ef" }} /> Add item
							</AddItemButton>
						)}
					</ItemsGrid>
					{deliverableIndex === editor.deliverables.length - 1 && (
						<GoLiveRow>
							<GoLiveTitle>
								<LiveGlyph>((•))</LiveGlyph>
								Go Live
								{!locked && (
									<IconAction
										type="button"
										aria-label="Edit go live date"
										onClick={() => editor.openGoLiveModal(deliverable)}
									>
										<EditOutlined />
									</IconAction>
								)}
							</GoLiveTitle>
							<DateChip>Live date: {formatDisplayDate(deliverable.liveDate) || "Select date"}</DateChip>
						</GoLiveRow>
					)}
				</DeliverableCard>
			))}
		</DeliverableList>
		{!locked && (
			<AddDeliverable type="button" onClick={editor.openAddDeliverable}>
				<PlusOutlined style={{ color: "#4460ef" }} /> Add deliverable
			</AddDeliverable>
		)}
	</TermsSection>
);
